import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_SLOTS = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];
const DAMAGE_TYPES = ['Roof Damage', 'Hurricane / Storm Damage', 'Water Damage', 'Denied / Underpaid Claim', 'Fire Damage', 'Other Damage'];

function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = Array(firstDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function BookingSection() {
  const { ref, isVisible } = useScrollAnimation();
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', address: '', damage_type: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const calDays = getCalendarDays(calYear, calMonth);

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const isDisabled = (day: number) => {
    const d = new Date(calYear, calMonth, day);
    const now = new Date(); now.setHours(0, 0, 0, 0);
    return d < now || d.getDay() === 0;
  };

  const selectedDateStr = selectedDay ? `${MONTHS[calMonth]} ${selectedDay}, ${calYear}` : '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDay || !selectedTime) { setError('Please select a date and time slot.'); return; }
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.damage_type) {
      setError('Please fill in all required fields.'); return;
    }
    if (formData.notes.length > 500) { setError('Notes must be 500 characters or less.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        damage_type: formData.damage_type,
        preferred_date: selectedDateStr,
        preferred_time: selectedTime,
        notes: formData.notes,
      });
      await fetch('https://readdy.ai/api/form/d6sqjojvrivh3q8km5sg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#0F1927] py-24 px-6 relative overflow-hidden" ref={ref}>
      <div
        className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #1B78D4 0%, transparent 50%), radial-gradient(circle at 80% 20%, #1B78D4 0%, transparent 40%)' }}
      />
      <div className="max-w-7xl mx-auto relative z-10">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-400/40 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
              <span className="text-red-300 text-sm font-semibold">LIMITED AVAILABILITY</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6">
              Don't Wait —
              <span className="text-[#5BA8E5] block">Insurance Deadlines Are Strict</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Every day you wait, evidence deteriorates and deadlines pass. Book your free inspection now — there's no cost and no obligation.
            </p>
            <div className="space-y-4 mb-10">
              {[
                { icon: 'ri-timer-flash-line', text: 'Most claims must be filed within 1 year of damage' },
                { icon: 'ri-eye-off-line', text: 'Evidence and documentation degrades over time' },
                { icon: 'ri-hourglass-line', text: 'Insurance companies intentionally delay your claim' },
                { icon: 'ri-money-dollar-circle-line', text: 'Initial offers are almost always far below fair value' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1B78D4]/10 rounded-lg flex-shrink-0 border border-[#1B78D4]/25">
                    <i className={`${item.icon} text-[#5BA8E5] text-lg`} />
                  </div>
                  <span className="text-gray-300 text-sm mt-2 leading-relaxed">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#1B78D4]/10 border border-[#1B78D4]/30 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                <span className="text-green-400 font-semibold text-sm">Next available: Tomorrow 9:00 AM</span>
              </div>
              <p className="text-gray-400 text-sm">Spots fill fast — we serve all of Florida and surrounding areas.</p>
            </div>
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-gray-500 text-sm mb-2">Have questions? Call us directly:</p>
              <a href="tel:+18132857094" className="text-[#5BA8E5] font-bold text-2xl flex items-center gap-2">
                <i className="ri-phone-fill" />
                (813) 285-7094
              </a>
            </div>
          </div>

          <div
            id="booking"
            className="bg-white rounded-2xl p-8"
          >
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-fill text-green-500 text-4xl" />
                </div>
                <h3 className="text-2xl font-extrabold text-[#0F1927] mb-3">Inspection Booked!</h3>
                <p className="text-gray-500 text-base mb-2">We'll confirm your appointment and send details to <strong>{formData.email}</strong></p>
                <p className="text-gray-400 text-sm">Expect a call from our team within 24 hours.</p>
                <a href="tel:+18132857094" className="inline-flex items-center gap-2 mt-6 text-[#1B78D4] font-semibold text-sm">
                  <i className="ri-phone-line" /> Questions? Call (813) 285-7094
                </a>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-extrabold text-[#0F1927] mb-1">Book Your Free Inspection</h3>
                <p className="text-gray-500 text-sm mb-6">Select a date, time, and fill in your details below.</p>

                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-600">
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    <span className="text-[#0F1927] font-bold text-sm">{MONTHS[calMonth]} {calYear}</span>
                    <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-600">
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {DAYS.map(d => <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calDays.map((day, i) => (
                      <div key={i} className="flex items-center justify-center">
                        {day ? (
                          <button
                            onClick={() => !isDisabled(day) && setSelectedDay(day)}
                            disabled={isDisabled(day)}
                            className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium transition-all duration-150 cursor-pointer ${
                              selectedDay === day
                                ? 'bg-[#1B78D4] text-white font-bold'
                                : isDisabled(day)
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-[#EBF4FD] hover:text-[#1B78D4]'
                            }`}
                          >
                            {day}
                          </button>
                        ) : <div />}
                      </div>
                    ))}
                  </div>
                </div>

                {selectedDay && (
                  <div className="mb-5">
                    <p className="text-sm font-semibold text-[#0F1927] mb-2">Select Time Slot</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {TIME_SLOTS.map(t => (
                        <button
                          key={t}
                          onClick={() => setSelectedTime(t)}
                          className={`py-2 px-2 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                            selectedTime === t
                              ? 'bg-[#1B78D4] border-[#1B78D4] text-white'
                              : 'border-gray-200 text-gray-600 hover:border-[#5BA8E5] hover:text-[#1B78D4]'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                  <input id="booking-name" name="name" type="text" placeholder="Full Name *" value={formData.name} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1B78D4] transition-colors" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input id="booking-phone" name="phone" type="tel" placeholder="Phone Number *" value={formData.phone} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1B78D4] transition-colors" />
                    <input id="booking-email" name="email" type="email" placeholder="Email Address *" value={formData.email} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1B78D4] transition-colors" />
                  </div>
                  <input id="booking-address" name="address" type="text" placeholder="Property Address *" value={formData.address} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1B78D4] transition-colors" />
                  <select id="booking-damage" name="damage_type" value={formData.damage_type} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1B78D4] transition-colors cursor-pointer">
                    <option value="">Type of Damage *</option>
                    {DAMAGE_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <textarea id="booking-notes" name="notes" placeholder="Additional notes (optional, max 500 characters)" value={formData.notes} onChange={handleChange} maxLength={500} rows={2} className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#1B78D4] transition-colors resize-none" />
                  {error && <p className="text-red-500 text-xs font-medium">{error}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#1B78D4] hover:bg-[#1560B8] disabled:bg-[#93C5F5] text-white font-extrabold py-4 rounded-lg transition-all duration-200 text-base cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? 'Booking...' : 'Confirm Free Inspection →'}
                  </button>
                  <p className="text-center text-gray-400 text-xs">
                    <i className="ri-shield-check-line text-green-500" /> No cost. No obligation. You'll receive a confirmation email instantly.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
